import { useEffect, useRef, useState } from 'react';

// Widget do Cloudflare Turnstile. Site key via env; em dev usa a chave de teste
// oficial (sempre passa). O token volta por onToken e vai no corpo do login/signup.
// `||` (não `??`) p/ cair no fallback também quando a env vier string vazia.
const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || '1x00000000000000000000AA';

// VITE_TURNSTILE_SITE_KEY=off desliga o widget — espelho do `TURNSTILE_SECRET`
// vazio que o servidor já aceita como "verificação desabilitada (dev)". Existe
// porque extensão de privacidade barra challenges.cloudflare.com, o token nunca
// chega, e o login aborta em silêncio: o dev fica sem entrar e sem saber por quê.
// Não abre brecha: quem valida é o servidor, e contra um TURNSTILE_SECRET de
// verdade este token de mentira leva 403 como qualquer outro.
const DISABLED = SITE_KEY === 'off';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
  reset: (id: string) => void;
}
declare global {
  interface Window { turnstile?: TurnstileApi }
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { scriptPromise = null; reject(new Error('turnstile: falha ao carregar')); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Turnstile emite um token de USO ÚNICO e curta validade (~300s). Por isso o widget
// precisa SE RENOVAR: na expiração refaz o desafio sozinho, e o pai pode forçar um
// token fresco via `resetKey` (ex.: depois que um login/cadastro falhou — senão o
// retry reusaria o token consumido e o backend devolveria captcha_failed). [[bug]]
export function Turnstile({ onToken, resetKey = 0 }: { onToken: (token: string | null) => void; resetKey?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onToken);
  cb.current = onToken;
  const widgetIdRef = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!DISABLED) return;
    cb.current('dev-turnstile-desligado');
  }, []);

  useEffect(() => {
    if (DISABLED) return;
    let cancelled = false;
    setFailed(false);
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          theme: 'auto',
          callback: (token: string) => cb.current(token),
          // Expirou: limpa o token e refaz o desafio p/ obter um novo (auto-renovação).
          'expired-callback': () => {
            cb.current(null);
            if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
          },
          'error-callback': () => { cb.current(null); setFailed(true); },
        });
      })
      .catch(() => { cb.current(null); setFailed(true); });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [attempt]);

  // Reset explícito do pai (resetKey muda) → descarta o token consumido e pega um novo.
  useEffect(() => {
    if (resetKey === 0) return; // 0 = valor inicial, não reseta no mount
    cb.current(null);
    if (window.turnstile && widgetIdRef.current) window.turnstile.reset(widgetIdRef.current);
  }, [resetKey]);

  if (DISABLED) {
    return (
      <div className="flex min-h-[65px] items-center justify-center rounded-md border border-dashed border-muted-foreground/30 text-xs text-muted-foreground">
        Verificação de segurança desligada (dev)
      </div>
    );
  }

  // Em vez de caixa vazia: mensagem clara quando o script é bloqueado
  // (adblock/extensão de privacidade costuma barrar challenges.cloudflare.com).
  if (failed) {
    return (
      <div className="flex min-h-[65px] flex-col items-center justify-center gap-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-center text-xs text-amber-800">
        <span>Não foi possível carregar a verificação de segurança. Desative bloqueadores/extensões (ou use uma janela anônima) e tente novamente.</span>
        <button type="button" onClick={() => setAttempt((a) => a + 1)} className="font-semibold underline">
          Tentar novamente
        </button>
      </div>
    );
  }
  return <div ref={ref} className="flex min-h-[65px] justify-center" />;
}
